import React, { forwardRef, useImperativeHandle, useRef } from 'react';
import HighchartsReact from 'highcharts-react-official';
import Highcharts from 'highcharts';
import { View } from 'react-native';
import SparkLine from './SparkLine';
import { LineCompStyling } from './style';
import { getLineChartSymbol, underscoreToText } from '../../utils';
import { handleHighChartsExport } from './utils';
import ReactDOMServer from 'react-dom/server';
import { tooltipStyling } from './style';

const MultiSeriesLineChart = forwardRef((props, ref) => {
  const { data, widgetData, categories = [], chartType = 'line' } = props;
  const styles = LineCompStyling();
  const chartRef = useRef(null);

  const actionButtonClick = e => {
    handleHighChartsExport(e, chartRef, widgetData);
  };
  useImperativeHandle(ref, () => ({ actionButtonClick }));

  function renderTooltip(data) {
    return (
      <div
        style={{
          color: data?.series?.color,
          ...tooltipStyling
        }}>
        <div>{underscoreToText(data.series.name)}</div>
        <div>{data.point.y?.toFixed(2)}</div>
      </div>
    );
  }

  const lineChartOptions = {
    chart: {
      type: 'line',
      style: {
        fontFamily: 'sans-serif'
      },
      spacing: [40, 0, 0, 0]
    },
    title: {
      text: ' ',
      align: 'left'
    },
    credits: {
      enabled: false
    },
    xAxis: {
      categories,
      tickmarkPlacement: 'on',
      lineWidth: 0,
      gridLineWidth: 0,
      accessibility: {
        rangeDescription: 'Months: Jan to Dec'
      }
    },
    yAxis: {
      title: {
        text: ' '
      },
      gridLineWidth: 1,
      labels: {
        format: '{value:.1f}'
      }
    },
    tooltip: {
      backgroundColor: 'black',
      borderRadius: 8,
      style: {
        color: '#fff',
        pointerEvents: 'none'
      },
      useHTML: true,
      formatter() {
        const header = `<div>
            ${this.category}
            <br /><br/>
          </div>`;
        const items = this.points
          .map(p =>
            ReactDOMServer.renderToString(
              renderTooltip({
                series: p.series,
                point: p.point
              })
            )
          )
          .join('');

        return header + items;
      },
      shared: true
    },
    legend: {
      enabled: false
    },

    plotOptions: {
      series: {
        label: {
          connectorAllowed: false
        },
        lineWidth: 2,
        cursor: 'pointer',
        marker: {
          enabled: true,
          radius: 4,
          symbol: function () {
            return getLineChartSymbol(this.index);
          }
        }
      }
    },

    series: data.map((series, index) => ({
      ...series,
      marker: { symbol: getLineChartSymbol(index) }
    })),
    exporting: { enabled: false },
    responsive: {}
  };

  const barChartOptions = {
    chart: {
      type: 'column',
      spacing: [40, 0, 0, 0]
    },
    title: {
      text: ' '
    },
    xAxis: {
      categories,
      crosshair: true,
      accessibility: {
        description: ' '
      }
    },
    yAxis: {
      min: 0,
      title: {
        text: ''
      }
    },
    legend: {
      enabled: false
    },
    tooltip: {
      backgroundColor: 'black',
      borderRadius: 8,
      useHTML: true,
      formatter() {
        const header = `<div>
            ${this.category}
            <br /><br/>
          </div>`;
        const items = this.points
          .map(p =>
            ReactDOMServer.renderToString(
              renderTooltip({
                series: p.series,
                point: p.point
              })
            )
          )
          .join('');
        return header + items;
      },
      shared: true
    },
    plotOptions: {
      column: {
        pointPadding: 0.2,
        borderWidth: 0
      }
    },
    exporting: { enabled: false },
    credits: {
      enabled: false
    },
    series: data
  };

  function getChartOption() {
    switch (chartType) {
      case 'line':
        return lineChartOptions;
      case 'column':
        return barChartOptions;
      default:
        return {};
    }
  }

  const chartOption = getChartOption();
  return (
    <View>
      <View style={styles.sparkLineContainer}>
        {data.map((item, index) => (
          <SparkLine chartType={chartType} key={item.name} index={index} {...item} />
        ))}
      </View>
      <HighchartsReact ref={chartRef} highcharts={Highcharts} options={chartOption} />
    </View>
  );
});
export default MultiSeriesLineChart;
